import React from 'react';
import { useLocation } from 'react-router-dom';
import { AuthData } from '../../../context/auth';
import {
	CancellationData,
	withCancellationContext,
} from '../../../context/CancellationProvider';
import { useRouteMatch } from '../../../util/useRouteMatch';
import LargeCenterSpinner from '../../LargeCenterSpinner';
import TreeNode, {
	TreeData,
	TreeItemData,
	TreeItemInterface,
} from '../../layouts/SidePanel/TreeNode';

interface Props {
	authData: AuthData;
	cancellationData: CancellationData;
}

export interface IssuedCancellationRoutes {
	INVOICES: string;
	INVOICE: string;
}

export const ISSUED_CANCELLATION_ROUTES: IssuedCancellationRoutes = {
	INVOICES: '/policy/:policyId/cancellations/:cancellationId/invoices',
	INVOICE:
		'/policy/:policyId/cancellations/:cancellationId/invoices/:invoiceId',
};

const IssuedCancellationSidebar = (props: Props) => {
	const { authData, cancellationData } = props;
	const { hasPermission } = authData;
	const { policy, selectedCancellation } = cancellationData;
	const { pathname } = useLocation();
	const routeMatch = useRouteMatch(
		Object.keys(ISSUED_CANCELLATION_ROUTES).map(
			(key) =>
				ISSUED_CANCELLATION_ROUTES[key as keyof IssuedCancellationRoutes],
		),
	);

	if (!policy || !selectedCancellation) {
		return <LargeCenterSpinner />;
	}

	const routeName = pathname.split('/').pop();
	const cancellationLink = `/policy/${policy.locator}/cancellations/${selectedCancellation.locator}`;
	const detailsLink = `${cancellationLink}/details`;
	const priceBreakdownLink = `${cancellationLink}/pricing`;
	const documentsLink = `${cancellationLink}/documents`;
	const invoicesLink = `${cancellationLink}/invoices`;
	const reinstatementsLink = `${cancellationLink}/reinstatements`;

	const getRootItem = () => {
		const children = ['Details', 'Price Breakdown', 'Documents'];

		if (hasPermission('invoice.fetch')) {
			children.push('Invoices');
		}

		if (hasPermission('reinstatement.fetch')) {
			children.push('Reinstatements');
		}

		return {
			id: 'root',
			children,
			hasChildren: true,
			isExpanded: true,
			isChildrenLoading: false,
			data: {
				title: 'root',
			},
		} as TreeItemInterface;
	};

	const getItems = () => {
		const isDetailsSelected =
			routeName === 'details' || routeName === 'auxData';

		const treeItems: TreeItemData = {
			root: getRootItem(),
			Details: {
				id: 'Details',
				children: [],
				hasChildren: false,
				isExpanded: true,
				isChildrenLoading: false,
				data: {
					title: 'Details',
					link: detailsLink,
					selected: isDetailsSelected,
				},
			},
			'Price Breakdown': {
				id: 'Price Breakdown',
				children: [],
				hasChildren: false,
				isExpanded: true,
				isChildrenLoading: false,
				data: {
					title: 'Price Breakdown',
					link: priceBreakdownLink,
					selected: routeName === 'pricing',
				},
			},
			Documents: {
				id: 'Documents',
				children: [],
				hasChildren: false,
				isExpanded: true,
				isChildrenLoading: false,
				data: {
					title: 'Documents',
					link: documentsLink,
					selected: routeName === 'documents',
				},
			},
			Invoices: {
				id: 'Invoices',
				children: [],
				hasChildren: false,
				isExpanded: false,
				isChildrenLoading: false,
				data: {
					title: 'Invoices',
					link: invoicesLink,
					selected:
						routeMatch?.pattern?.path === ISSUED_CANCELLATION_ROUTES.INVOICES ||
						routeMatch?.pattern?.path === ISSUED_CANCELLATION_ROUTES.INVOICE,
				},
			},
			Reinstatements: {
				id: 'Reinstatements',
				children: [],
				hasChildren: false,
				isExpanded: false,
				isChildrenLoading: false,
				data: {
					title: 'Reinstatements',
					link: reinstatementsLink,
					selected: routeName === 'reinstatements',
				},
			},
		};

		return {
			...treeItems,
		};
	};

	const items = getItems();
	const initializeTree = () => {
		const sidePanelData: TreeData = {
			rootId: 'root',
			items,
		};

		return sidePanelData;
	};

	const tree = initializeTree();

	return <TreeNode defaultExpanded={[]} treeData={tree} />;
};

export default withCancellationContext<Props>(IssuedCancellationSidebar);
